import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useTranslate } from '../../mixins/translate/translate';
import { loginAs } from '../../store/authentication/actions';
import { fetchGet, fetchPost } from '../../store/fetch';
import { Roles } from '../../utils/enums/Roles';
import { IconButton } from '../common/iconButtons/IconButton';
import { IconEditButton } from '../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import { Table, TableBody, TableButtons, TableData, TableHead, TableHeader, TableHeadRow, TableRow } from '../elements';
import { useUserHasRole } from '../utils/authorization/useUserHasRole';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import { useReactAsync } from '../utils/useReactAsync';

const maxLoginAttempts = 5;

interface IUser {
    id: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    lastLoginOn: Date;
    loginAttempts: number;
    status: string;
    isReadOnly: boolean;
    isAccountManager: boolean;
    isConfigurationManager: boolean;
    hasOtpEnabled: boolean;
    companyCount: number;
}

async function loadUsers({ companyId }: { companyId: string }) {
    const url = `/api/users?companyId=${companyId}`;
    const result = await fetchGet(url);
    return result.json.items as IUser[];
}

// eslint-disable-next-line @typescript-eslint/require-await

export function UserList(props: { companyId: string }) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const { data, reload } = useReactAsync(loadUsers, { companyId: props.companyId }, []);

    async function removeUser(userId: string) {
        await fetchPost(`/api/users/${userId}/removeCompany`, {}, { companyId: props.companyId });
        await reload();
    }
    // eslint-disable-next-line @typescript-eslint/require-await
    async function loginAsUser(userId: string) {
        navigate('/logOut');
        dispatch(loginAs(userId, props.companyId) as any);
        navigate('/dashboard');
    }

    return (
        <>
            <Table>
                <TableHead>
                    <TableHeadRow light={true}>
                        <TableHeader>{translate('common.firstName')}</TableHeader>
                        <TableHeader>{translate('common.lastName')}</TableHeader>
                        <TableHeader>{translate('common.emailAddress')}</TableHeader>
                        <TableHeader>{translate('common.lastLogin')}</TableHeader>
                        <TableHeader></TableHeader>
                        <TableHeader></TableHeader>
                    </TableHeadRow>
                </TableHead>
                <TableBody>
                    {data &&
                        data.map((item) => (
                            <TableRow key={item.id}>
                                <TableData>{item.firstName}</TableData>
                                <TableData>{item.lastName}</TableData>
                                <TableData>{item.emailAddress}</TableData>
                                <TableData>
                                    <NullableFormattedDate value={item.lastLoginOn} />
                                </TableData>
                                <TableData>
                                    {(item.loginAttempts > maxLoginAttempts || item.status !== 'enabled') && <i className="fa fa-lock mr5"></i>}
                                    {!item.isReadOnly && <i className="fa fa-pencil mr5"></i>}
                                    {item.isAccountManager && <i className="fa fa-user mr5"></i>}
                                    {item.isConfigurationManager && <i className="fa fa-wrench mr5"></i>}
                                    {item.hasOtpEnabled && <i className="fa fa-qrcode"></i>}
                                </TableData>
                                <TableButtons>
                                    <IconEditButton onClick={() => navigate(`/companies/${item.id}/edituser?companyId=${props.companyId}`)} />
                                    <IconRemoveButton
                                        removeQuestion={
                                            item.companyCount <= 1
                                                ? translate('common.confirmDelete', `${item.firstName} ${item.lastName}`)
                                                : translate('user.confirmRemoveFromCompany', `${item.firstName} ${item.lastName}`)
                                        }
                                        onClick={async () => removeUser(item.id)}
                                    />
                                    {isAdmin && <IconButton onClick={async () => loginAsUser(item.id)} iconClass="fa fa-eye" />}
                                </TableButtons>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </>
    );
}
